import { BaseApiClient } from './base-client'
import { API_ROUTES, ApiResponse, interpolateRoute } from '@super/shared-types'
import { withErrorHandling } from '../utils'
import type {
  AgentWithVoiceSettings,
  CreateAgentRequest,
  UpdateAgentRequest,
} from './agents.client'

/**
 * Admin-only API client for agent operations.
 * Use this client when acting as SuperAdmin (e.g. managing agents for any company).
 * All methods require admin privileges.
 */
export class AgentsAdminApiClient extends BaseApiClient {
  /**
   * Get all agents for a company (Admin only).
   * @param companyId - The ID of the company to get agents for
   * @returns Array of agents with voice settings
   */
  async getAgentsForCompany(
    companyId: string,
  ): Promise<AgentWithVoiceSettings[]> {
    const result = await withErrorHandling(
      async () => {
        const queryParams = { companyId }
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings[]>
        >(`${API_ROUTES.ADMIN.AGENTS.GET}${this.buildQueryString(queryParams)}`)
        return response.data || []
      },
      {
        on404: { fallback: [] },
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching agents for company',
      },
    )
    return result || []
  }

  /**
   * Create agent for a company (Admin only).
   * @param data - The data to create the agent with (must include companyId)
   * @returns The created agent with voice settings
   */
  async createAgentForCompany(
    data: CreateAgentRequest & { companyId: string },
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings>
        >(API_ROUTES.ADMIN.AGENTS.CREATE, {
          method: 'POST',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating agent for company',
      },
    )
  }

  /**
   * Update agent for a company (Admin only).
   * @param id - The ID of the agent to update
   * @param data - The data to update the agent with
   * @returns The updated agent with voice settings
   */
  async updateAgentForCompany(
    id: string,
    data: UpdateAgentRequest,
  ): Promise<AgentWithVoiceSettings | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<AgentWithVoiceSettings>
        >(interpolateRoute(API_ROUTES.ADMIN.AGENTS.UPDATE, { id }), {
          method: 'PUT',
          body: JSON.stringify(data),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating agent for company',
      },
    )
  }

  /**
   * Delete agent for a company (Admin only, soft delete).
   * @param id - The ID of the agent to delete
   * @returns Success status
   */
  async deleteAgentForCompany(id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        await this.request<ApiResponse<{ success: boolean }>>(
          interpolateRoute(API_ROUTES.ADMIN.AGENTS.DELETE, { id }),
          {
            method: 'DELETE',
          },
        )
        return true
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting agent for company',
      },
    )
    return result ?? false
  }
}

export const agentsAdminApiClient = new AgentsAdminApiClient()
